import { Box, Button, CircularProgress } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import CloudServerIcon from 'components/svg/cloud-server'
import UploadArea from 'components/upload-area'
import { useAlert } from 'contexts/alert'
import { useProject } from 'contexts/project'
import { Fragment, useState } from 'react'
import { useHistory } from 'react-router-dom'
import api from 'services/api'
import { BaseResponse, loadSingle } from 'services/requests'
import donwloadFileWithAxiosResponse from 'utils/download-filte'
import AlertBox from './alert-box'
import DataView from './data-view'
import { useStyles } from './styles'

export interface ILayoutAppMissingData {
  numberOfLifesEsocialMissingData: number
  esocialMissingData: number
  numberOfLifesNonEsocialMissingData: number
  nonEsocialMissingData: number
}

const MinhaBaseIncluirDadosFaltantes = () => {
  const [files, setFiles] = useState<File[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const { data: project } = useProject()
  const { openAlert } = useAlert()

  const classes = useStyles()
  const history = useHistory()

  const { data, isLoading, isPending, isFetching, refetch } = useQuery({
    queryKey: ['apps', 'layout', 'main', project.id],
    queryFn: () => loadSingle<BaseResponse<ILayoutAppMissingData>>(`/apps/layouts/main/${project.id}`),
    select: (data) => data.data.data,
  })

  const handleCancel = () => history.push(`/projects/${project.id}`)

  const handleSubmitFiles = async () => {
    try {
      setLoading(true)
      const formData = new FormData()
      formData.append('dados-faltantes', files[0])

      await api.put(`/apps/layouts/non-esocial/${project.id}`, formData)
      await refetch()
      setFiles([])
      openAlert('Dados faltantes carregados com sucesso', 'success')
    } catch (error) {
      openAlert('Um erro ocorreu ao carregar os dados faltantes.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadDadosFaltantes = async (type: 'esocial' | 'not-esocial') => {
    openAlert('O download do arquivo será iniciado em breve', 'success')

    let response: any

    if (type === 'esocial') {
      response = await api.get(`/apps/layouts/esocial/${project.id}`, {
        responseType: 'blob',
      })
    } else if (type === 'not-esocial') {
      response = await api.get(`/apps/layouts/non-esocial/${project.id}`, {
        responseType: 'blob',
      })
    }

    donwloadFileWithAxiosResponse(response)
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.box}>
        <AlertBox
          title='Alguns dados obrigatórios podem estar incompletos no eSocial'
          actionText='Baixar lista de dados faltantes eSocial'
          onActionClick={() => handleDownloadDadosFaltantes('esocial')}
          subTitle={
            <span>
              Faça download da lista de dados faltantes e atualize-os no{' '}
              <a
                href='https://login.esocial.gov.br/login.aspx'
                target='_blank'
              >
                eSocial
              </a>
            </span>
          }
        />

        <DataView
          lifes={data?.numberOfLifesEsocialMissingData ?? 0}
          missingData={data?.esocialMissingData ?? 0}
          title='Dados eSocial'
          isLoading={isLoading || isPending || isFetching}
        />
      </Box>

      <Box className={classes.box}>
        {!loading && (
          <Fragment>
            <AlertBox
              title='Alguns dados obrigatórios podem estar incompletos (e não existem no eSocial)'
              subTitle='Faça download da lista de dados faltantes, complete-os no arquivo baixado e envie aqui para completá-los.'
              actionText='Baixar lista de dados faltantes'
              onActionClick={() => handleDownloadDadosFaltantes('not-esocial')}
            />

            <DataView
              lifes={data?.numberOfLifesNonEsocialMissingData ?? 0}
              missingData={data?.nonEsocialMissingData ?? 0}
              title='Dados que não estão no eSocial'
              isLoading={isLoading || isPending || isFetching}
            />

            <UploadArea
              files={files}
              setFiles={setFiles}
            />
            <Box className={classes.actions}>
              <Button
                type='button'
                variant='outlined'
                className={classes.cancelButton}
                onClick={handleCancel}
              >
                Cancelar
              </Button>
              <Button
                type='submit'
                variant='contained'
                disabled={files.length === 0}
                className={classes.submitButton}
                onClick={handleSubmitFiles}
              >
                Enviar
              </Button>
            </Box>
          </Fragment>
        )}

        {loading && (
          <Box className={classes.boxLoading}>
            <CloudServerIcon />
            <span>Estamos carregando os dados. Por favor, aguarde um momento.</span>
            <CircularProgress
              color='primary'
              size={32}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default MinhaBaseIncluirDadosFaltantes
